import { IT_ROLES } from './utils';

// One table per page: where it lives, how it appears in the sidebar, and who
// may reach it.
//
// The two things it declares are deliberately separate, because the old single
// table conflated them and that is what opened a hole:
//
//   roles      — access policy. Enforced on the route as well as the nav. A
//                role outside this list is redirected away from the URL.
//   hiddenFor  — sidebar curation only, NOT access control. The page stays
//                reachable; it just isn't linked for roles that have no day-to-
//                day use for it.
//
// Before the split there was one allowlist doing both jobs by implication, and
// only Integrations had a matching route guard. The Audit Log was absent from
// HR's and read-only's list and so never appeared in their sidebar — while
// /audit answered a typed or bookmarked URL from either of them perfectly well.
//
// Keeping `hiddenFor` explicitly toothless matters for a second reason: those
// lists are older than some of these pages. Map and Damage Reports were both
// added after the roles were first set up and nobody revisited the lists, so
// their absence from HR's is an oversight rather than a decision. Omission
// hides a page here; it does not forbid it. Policy has to be stated in `roles`
// to bite.
//
// Where `roles` appears, it is written to match the API's own authorize() call
// for the endpoints that page depends on — the API is the enforcement layer, so
// a page offered to a role the server refuses is just a broken link. The three
// that are actually gated server-side:
//
//   /map           GET /api/assets/map   it_admin, it_staff, supervisor
//   /audit         GET /api/audit        it_admin, it_staff
//   /integrations  POST /api/sync/*      it_admin, it_staff
//
// /reports is the exception: its `roles` list is frontend-only, because
// /api/reports/* carries no authorize() at all. Same for every page with only a
// `hiddenFor` — assets, employees, locations, transfers and damage are open to
// any authenticated role at the API, so hiding them is presentation, not
// protection. Do not mistake this table for a security boundary; it keeps the
// UI honest about what the server will answer.
//
// The `hiddenFor` lists have had a pass against the API. What remains is HR's,
// which is the original stale list and is left alone on purpose — every entry
// below says whether it is a decision or an inheritance.
export const NAV = [
  { to: '/dashboard',    icon: '◈',   label: 'Dashboard',
    // HR lands here from the index route and the page has an HR branch of its
    // own (pending returns), so the missing link is likely just stale.
    hiddenFor: ['hr'] },

  { to: '/assets',       icon: '▦',   label: 'Assets',
    hiddenFor: ['hr'] },

  // Mirrors the API: GET /api/assets/map carries
  // authorize('it_admin', 'it_staff', 'supervisor'), so this is real policy
  // rather than curation and belongs in `roles`.
  //
  // Read-only had /map in its old nav allowlist and the page has never worked
  // for them — the endpoint 403s and the map renders "Could not load device
  // locations". Excluding them here replaces a broken link with no link. It
  // takes away nothing they could actually see: the aggregate view was always
  // refused. (Per-device location still reaches them through
  // /assets/:id/hexnode-detail, which the API does authorize for every role.)
  { to: '/map',          icon: '🗺️',  label: 'Map',
    roles: [...IT_ROLES, 'supervisor'] },

  { to: '/employees',    icon: '◉',   label: 'Employees' },

  { to: '/locations',    icon: '◎',   label: 'Locations',
    hiddenFor: ['hr'] },

  // Read-only was dropped from this list deliberately, not by omission.
  //
  // The page is a filtered table with no mutations on it, /api/damage carries no
  // authorize(), and `hiddenFor` does not guard the route — so read-only users
  // could already read every damage report by typing the URL. Hiding the link
  // protected nothing and only made the page hard to find for people entitled to
  // it, who already see the assets and employees those reports concern.
  //
  // Restricting it for real would mean an authorize() call in the API and a
  // `roles` list here to match. That option stayed on the table and was not
  // taken; if it is ever wanted, the API is the side that has to change first.
  { to: '/damage',       icon: '⚠',   label: 'Damage Reports',
    // HR's omission is still the original oversight — this list predates the
    // page. Left as-is: nothing here says HR wants it.
    hiddenFor: ['hr'] },

  // Supervisor is unrestricted by default, so without a `roles` list it fell
  // through to seeing Reports — whose tabs are all gated on IT/HR, leaving a
  // header over nothing.
  { to: '/reports',      icon: '📊',  label: 'Reports',
    roles: [...IT_ROLES, 'hr'] },

  { to: '/integrations', icon: '🔌',  label: 'Integrations',
    // Runs the syncs, including the Workday one that can flag devices for
    // return across the whole company.
    roles: IT_ROLES },

  // Mirrors the API: GET /api/audit carries authorize('it_admin', 'it_staff').
  //
  // Supervisor is excluded, which is a change. They could open this page and
  // the endpoint refused them — but AuditLog only console.errors a failed load
  // and leaves `entries` empty, so the page told them the audit log was empty
  // rather than that they could not see it. Silently wrong beats no worse than
  // an error only until someone acts on it.
  //
  // Granting supervisors access instead would not be a one-line change: they
  // are scoped to hierarchy groups (see resolveScopes in the API's auth
  // middleware) and audit_log is company-wide and unscoped, so opening it would
  // hand them every change at every site. Scoping that query first is the
  // prerequisite, and it is a decision about what supervisors should see, not a
  // mismatch to paper over.
  { to: '/audit',        icon: '📋',  label: 'Audit Log',
    roles: IT_ROLES },
];

// Where a denied route sends someone.
export const FALLBACK_PATH = '/dashboard';

// Detail pages inherit their section's rule: /assets/:id is governed by the
// /assets entry. Without the prefix match, restricting a list would still leave
// every individual record open to a role that can't reach the list.
function navEntryFor(path) {
  return NAV.find(item => path === item.to || path.startsWith(item.to + '/'));
}

// Whether a role may open a path — `roles` only. Paths with no nav entry
// (/login, /auth/callback) aren't governed here.
export function canAccessPath(role, path) {
  // Denied routes redirect to FALLBACK_PATH, so it has to stay reachable for
  // every role or the redirect loops.
  if (path === FALLBACK_PATH) return true;
  const item = navEntryFor(path);
  if (!item?.roles) return true;
  return item.roles.includes(role);
}

// The sidebar for a role, in NAV order — `roles` and `hiddenFor` together.
export function navForRole(role) {
  return NAV.filter(item => {
    if (item.roles && !item.roles.includes(role)) return false;
    if (item.hiddenFor?.includes(role)) return false;
    return true;
  });
}
